<template>
  <NPopover
    v-if="!isEmpty(options)"
    ref="popoverRef"
    :animated="false"
    class="dynamic-value-tree-select"
    :on-clickoutside="close"
    placement="bottom"
    :show="showPopover"
    :show-arrow="false"
    trigger="manual"
    :width="260"
  >
    <template #trigger>
      <CButton
        class="dynamic-value-tree-select__trigger"
        :themeOverrides="themeOverrides?.[selectedOverride]"
        @click="handleTriggerClick"
      >
        <CIcon class="mr-1" icon="brackets-curly" size="16px" />
        <CIcon icon="chevron-down" size="12px" />
      </CButton>
    </template>
    <NTreeSelect
      ref="treeSelectRef"
      block-line
      filterable
      :multiple="allowMultiple"
      :on-update:value="onSelect"
      :options="options"
      :override-default-node-click-behavior="override"
      :value="selectedValues"
      @keydown.enter.capture.prevent.stop="close"
    />
  </NPopover>
</template>

<script lang="ts" setup>
import { computed, ref, nextTick, watch, onBeforeUnmount, type StyleValue } from 'vue'
import { NTreeSelect, NPopover, type TreeSelectOverrideNodeClickBehavior } from 'naive-ui'
import { isEmpty } from 'lodash-es'

import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'

import useCrmShape from '@canvas-builder/composables/useCrmShape'

import { loadCrmShapeFromApps } from '@lib/composables/useInstalledApps'
import { transformToTree } from '@lib/util/tree-transformation.util'

const props = withDefaults(
  defineProps<{
    allowMultiple?: boolean
    data: Record<string, any>
    themeOverrides?: { active: StyleValue; default: StyleValue }
    allowArrays?: boolean
  }>(),
  {
    allowArrays: true,
    allowMultiple: true,
  },
)

const emit = defineEmits<{ select: [value: string] }>()

const popoverRef = ref<InstanceType<typeof NPopover> | null>(null)
const treeSelectRef = ref<InstanceType<typeof NTreeSelect> | null>(null)
const selectedOverride = ref<'default' | 'active'>('default')
const showPopover = ref(false)
const selectedValues = ref<string[]>([])
const isProcessingSelections = ref(false)

const override: TreeSelectOverrideNodeClickBehavior = ({ option }) => {
  if (option.children) {
    return 'toggleExpand'
  }
  return 'default'
}

const { setRealTimeExtraCrmShape } = useCrmShape()

const treeData = computed(() => transformToTree(props.data ?? {}))

const options = computed(() => {
  if (props.allowArrays === false) {
    return filterArrayOptions(treeData.value.options)
  }

  return treeData.value.options
})

const arrayDict = computed(() => treeData.value.arrayDict)

function filterArrayOptions(opts: any[]): any[] {
  return opts.reduce((acc: any[], option) => {
    if (arrayDict.value[option.key]) return acc

    const transformedOption = {
      ...option,
      children: option.children ? filterArrayOptions(option.children) : undefined,
    }

    if (transformedOption.children && transformedOption.children.length === 0) return acc

    acc.push(transformedOption)
    return acc
  }, [])
}

function onSelect(selection: string | string[]) {
  selectedValues.value = Array.isArray(selection) ? selection : [selection]

  if (props.allowMultiple === false) {
    close()
  }
}

function processSelectedValues() {
  if (isProcessingSelections.value || selectedValues.value.length === 0) {
    return
  }

  isProcessingSelections.value = true

  try {
    selectedValues.value.forEach((selection) => {
      if (arrayDict.value[selection]) {
        emit(
          'select',
          `{{#each ${selection}}}\n` +
            Object.keys(arrayDict.value[selection]?.[0])
              .map((key) => `{{{this.${key}}}}\n`)
              .join(' ') +
            '{{/each}}',
        )
      } else {
        emit('select', `{{${selection}}}`)
      }
    })
  } finally {
    isProcessingSelections.value = false
  }
}

function cleanupState() {
  selectedOverride.value = 'default'
  selectedValues.value = []
  isProcessingSelections.value = false
}

async function handleShow(show: boolean) {
  if (show) {
    await nextTick()

    const element: HTMLElement | null = treeSelectRef.value?.$el.querySelector(
      '.dynamic-value-tree-select.n-popover .n-base-selection',
    )
    element?.click()
  }
}

async function handleTriggerClick() {
  const realtimeCrmShape = await loadCrmShapeFromApps().catch(() => ({}))
  setRealTimeExtraCrmShape(realtimeCrmShape)
  showPopover.value = !showPopover.value
  handleShow(showPopover.value)
  if (showPopover.value) {
    selectedOverride.value = 'active'
  }
}

function close() {
  // Process selections immediately before closing to avoid race conditions
  if (selectedValues.value.length > 0) {
    processSelectedValues()
  }

  // forcing this event to be executed after handleTriggerClick
  setTimeout(() => {
    showPopover.value = false
  }, 0)
}

watch(showPopover, (newValue, oldValue) => {
  if (oldValue === true && newValue === false) {
    // Only cleanup state since selections were already processed in close()
    cleanupState()
  }
})

// Ensure selections are processed if component is unmounted while popover is open
onBeforeUnmount(() => {
  if (showPopover.value && selectedValues.value.length > 0) {
    processSelectedValues()
  }
})
</script>
